import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle, LockKey, ArrowRight, Clock } from "@phosphor-icons/react";
import API from "../../../lib/api";
import { Button } from "../../ui/button";
import { useAuth } from "../../../contexts/AuthContext";
import { hasFeature, FEATURE_META } from "../../../lib/features";
import { Pill, fmtDT } from "../hsUtils";
import { TONE_CLASSES } from "../../assets/assetUtils";
import { KindPill } from "./libraryUtils";
import { loadUpdates } from "./LibraryUpdates";

const SEEN_KEY = "hsLibraryWhatsNewSeen";

/** H&S Overview tile: what's new in the Template library and how many of your copies have a newer version. */
export function LibraryOverviewCard({ setTab }) {
  const { user } = useAuth();
  const on = hasFeature(user, "hs_library");
  const [feed, setFeed] = useState(null);
  const [updates, setUpdates] = useState(null);
  useEffect(() => {
    if (!on) return;
    API.get("/hs/library/whats-new", { params: { limit: 4 } }).then((r) => setFeed(r.data.entries || [])).catch(() => setFeed([]));
    loadUpdates().then((d) => setUpdates(d.updates || [])).catch(() => setUpdates([]));
  }, [on]);

  if (!on) {
    const meta = FEATURE_META.hs_library;
    return (
      <div className="rounded-lg border border-dashed border-border bg-card p-4" data-testid="hs-overview-library-locked">
        <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><LockKey size={16} /> {meta.label}</h3>
        <p className="text-xs text-muted-foreground">{meta.blurb}</p>
        <Button asChild size="sm" variant="outline" className="mt-2 h-7 text-xs"><Link to="/platform/billing?add=hs_library" data-testid="hs-overview-library-cta">Add to plan</Link></Button>
      </div>
    );
  }
  const seenAt = typeof window !== "undefined" ? window.localStorage.getItem(SEEN_KEY) || "" : "";
  const unseen = (feed || []).filter((e) => e.published_at > seenAt).length;
  const due = (updates || []).filter((u) => u.copy?.due_for_review).length;
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="hs-overview-library">
      <h3 className="text-sm font-semibold mb-2 flex items-center gap-2 flex-wrap"><Sparkle size={16} weight="fill" className="text-primary" /> Template library
        {unseen > 0 && <Pill tone={TONE_CLASSES.soon} testid="hs-overview-library-unseen">{unseen} new since your last look</Pill>}
        {updates?.length > 0 && <Pill tone={due ? TONE_CLASSES.overdue : TONE_CLASSES.ok} testid="hs-overview-library-updates">{updates.length} update{updates.length === 1 ? "" : "s"} waiting{due ? ` · ${due} due for review` : ""}</Pill>}
      </h3>
      {feed === null ? <p className="text-xs text-muted-foreground">Loading…</p> : feed.length === 0 ? <p className="text-xs text-muted-foreground">Nothing published yet.</p> : (
        <ul className="divide-y divide-border">
          {feed.map((e) => (
            <li key={`${e.item_id}-${e.version}`} className="py-1.5 flex items-center gap-2 text-xs" data-testid={`hs-overview-library-entry-${e.item_id}-${e.version}`}>
              <KindPill kind={e.kind} />
              <span className="truncate flex-1"><b className="font-medium">{e.title}</b> <span className="text-muted-foreground">· {e.change === "new" ? "new" : `updated to v${e.version}`}{e.my_copies ? " · in your workspace" : ""}</span></span>
              <span className="text-muted-foreground shrink-0 inline-flex items-center gap-1"><Clock size={11} /> {fmtDT(e.published_at)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setTab("template-library")} data-testid="hs-overview-library-open">Open the Template library <ArrowRight size={12} className="ml-1" /></Button>
        {updates?.length > 0 && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setTab("template-library")} data-testid="hs-overview-library-see-updates">See the {updates.length} update{updates.length === 1 ? "" : "s"}</Button>}
      </div>
    </div>
  );
}
